import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Grid,
  Alert,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import TagCloud from '../TagCloud';
import {
  validateTagCloudData,
  createSampleTagCloudData,
} from '../../utils/tagCloudUtils';
import Button from './Button';
import TextField from './TextField';
import Tabs from './Tabs';
import Card from './Card';
import { useTranslation } from 'react-i18next';

const TagCloudEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  // Инициализация данных
  useEffect(() => {
    try {
      let initialItems = [];

      // Если есть содержимое, пробуем его распарсить
      if (content) {
        // Используем утилиту для валидации данных
        const validData = validateTagCloudData(content, 'TagCloudEditor');

        if (validData.length > 0) {
          initialItems = validData;
          setError('');
        } else {
          // Если контент есть, но он не валиден, создаем пример и выводим ошибку
          setError(t('lessons:editor.tagCloud.invalidFormatError'));
          initialItems = createSampleTagCloudData(3);
        }
      } else {
        // Если контента нет, создаем пример
        initialItems = createSampleTagCloudData(3);
      }

      setItems(initialItems);
    } catch (err) {
      console.error(t('lessons:editor.tagCloud.initError'), err);
      setError(t('lessons:editor.tagCloud.initErrorWithSample'));
      setItems(createSampleTagCloudData(3));
    }
  }, [content, t]);

  // Обновление родительского компонента
  const updateContent = (newItems) => {
    try {
      setItems(newItems);
      onChange(JSON.stringify(newItems));
    } catch (err) {
      console.error(t('lessons:editor.tagCloud.updateError'), err);
      setError(t('lessons:editor.tagCloud.updateFailed'));
    }
  };

  // Добавление нового элемента
  const handleAddItem = () => {
    const newItem = {
      term: t('lessons:editor.tagCloud.newTerm', { number: items.length + 1 }),
      definition: t('lessons:editor.tagCloud.newDefinition', {
        number: items.length + 1,
      }),
    };

    const newItems = [...items, newItem];
    updateContent(newItems);

    // Переключаемся на вкладку редактирования
    setTabIndex(0);
  };

  // Обновление элемента
  const handleUpdateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    updateContent(newItems);
  };

  // Удаление элемента
  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    updateContent(newItems);
  };

  // Смена вкладки
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Редактор - вкладка с формой
  const renderEditor = () => (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {items.map((item, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="subtitle1">
              {t('lessons:editor.tagCloud.pairNumber', { number: index + 1 })}
            </Typography>
            <IconButton
              color="error"
              onClick={() => handleRemoveItem(index)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                required
                label={t('lessons:editor.tagCloud.term')}
                value={item.term}
                onChange={(e) =>
                  handleUpdateItem(index, 'term', e.target.value)
                }
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                required
                label={t('lessons:editor.tagCloud.definition')}
                value={item.definition}
                onChange={(e) =>
                  handleUpdateItem(index, 'definition', e.target.value)
                }
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Card>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddItem}
        fullWidth
        sx={{ mt: 2 }}
      >
        {t('lessons:editor.tagCloud.addTerm')}
      </Button>
    </Box>
  );

  // Превью - вкладка с предпросмотром
  const renderPreview = () => (
    <Box sx={{ p: 2 }}>
      {items.length === 0 ? (
        <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
          {t('lessons:editor.tagCloud.noTermsForPreview')}
        </Typography>
      ) : (
        <TagCloud
          options={items}
          onChange={() => {}}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          bgcolor: 'background.default',
          borderRadius: '16px',
          p: '4px',
          mb: 2,
        }}
      >
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label={t('lessons:editor.tagCloud.editor')} />
          <Tab
            label={t('lessons:editor.tagCloud.preview')}
            icon={<PreviewIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {tabIndex === 0 && renderEditor()}
      {tabIndex === 1 && renderPreview()}

      {tabIndex === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t('lessons:editor.tagCloud.instructionsInfo')}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default TagCloudEditor;
